import React, { useState, useEffect, useCallback } from 'react';
import { Mosaic, MosaicWindow, MosaicContext, getLeaves } from 'react-mosaic-component';
import 'react-mosaic-component/react-mosaic-component.css';
import './editor-mosaic-theme.css';
import JsonViewer from './JsonViewer';
import MarkdownViewer from './MarkdownViewer';
import GitGraphViewer from './GitGraphViewer';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';

const DocumentIcon = ({ color }) => (
  <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 16 16" style={{ color }}>
    <path d="M13.71,4.29l-3-3L10,1H4C3.45,1,3,1.45,3,2v12c0,0.55,0.45,1,1,1h8c0.55,0,1-0.45,1-1V5L13.71,4.29z M10,2.41L11.59,4H10V2.41z M12,14H4V2h5v3h3V14z" />
  </svg>
);

const EditorPanel = ({ file, content, theme, wordWrap, editorFontSize }) => {
  const getLanguage = (filename) => {
    if (filename.endsWith('.js') || filename.endsWith('.jsx')) return 'javascript';
    if (filename.endsWith('.ts') || filename.endsWith('.tsx')) return 'typescript';
    if (filename.endsWith('.html')) return 'html';
    if (filename.endsWith('.css')) return 'css';
    if (filename.endsWith('.py')) return 'python';
    return 'plaintext';
  };

  const codeStyle = theme === 'light' ? vs : vscDarkPlus;

  return (
    <div className="h-full bg-[var(--bg-primary)] overflow-auto custom-scrollbar">
      {file.name.endsWith('.json') ? (
        <JsonViewer content={content} theme={theme} editorFontSize={editorFontSize} />
      ) : file.name.endsWith('.md') ? (
        <MarkdownViewer content={content} theme={theme} editorFontSize={editorFontSize} />
      ) : file.name.endsWith('.gitgraph') ? (
        <GitGraphViewer content={content} />
      ) : (
        <SyntaxHighlighter
          language={getLanguage(file.name)}
          style={codeStyle}
          showLineNumbers={true}
          wrapLines={wordWrap === 'on'}
          wrapLongLines={wordWrap === 'on'}
          customStyle={{
            margin: 0,
            padding: '1rem',
            backgroundColor: 'transparent',
            fontSize: `${editorFontSize || 14}px`,
            lineHeight: '1.5',
            fontFamily: "'JetBrains Mono', 'Consolas', monospace"
          }}
          lineNumberStyle={{
            minWidth: '3em',
            paddingRight: '1em',
            color: '#858585',
            textAlign: 'right'
          }}
        >
          {content}
        </SyntaxHighlighter>
      )}
    </div>
  );
};

const TabBar = ({ tabs, activeTabName, onTabClick, onCloseTab, isActiveGroup, groupPath }) => {
  return (
    <div
      className={`flex overflow-x-auto min-h-[35px] ${isActiveGroup ? 'bg-[#1e1e1e]' : 'bg-[#252526]'}`}
      style={{
        borderBottom: '1px solid var(--border-color, #1e1e1e)',
        scrollbarWidth: 'none'
      }}
    >
      {tabs.map((tab) => (
        <div
          key={tab.name}
          className={`flex items-center px-4 py-2 cursor-pointer group ${activeTabName === tab.name
            ? 'bg-[#1e1e1e] text-[var(--text-primary)]'
            : 'bg-[#2d2d2d] text-[var(--text-secondary)] hover:bg-[#2d2d2d] opacity-80 hover:opacity-100'
            }`}
          onClick={() => onTabClick(groupPath, tab.name)}
          style={{
            minWidth: '120px',
            maxWidth: '200px',
            flexShrink: 0,
            backgroundColor: activeTabName === tab.name ? 'var(--bg-primary, #1e1e1e)' : '',
            borderRight: '1px solid var(--border-color, #252526)',
            borderTop: activeTabName === tab.name && isActiveGroup ? '2px solid #007acc' : '2px solid transparent'
          }}
        >
          {/* Default file icon */}
          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 16 16" style={{
            color: tab.name.endsWith('.js') || tab.name.endsWith('.json') ? '#cbcb41' : tab.name.endsWith('.md') ? '#69b0fa' : 'currentColor'
          }}>
            <path d="M13.71,4.29l-3-3L10,1H4C3.45,1,3,1.45,3,2v12c0,0.55,0.45,1,1,1h8c0.55,0,1-0.45,1-1V5L13.71,4.29z M10,2.41L11.59,4H10V2.41z M12,14H4V2h5v3h3V14z" />
          </svg>
          <span className="truncate flex-1 text-sm font-sans select-none" style={{
            fontStyle: tab.name === 'settings.json' ? 'italic' : 'normal'
          }}>{tab.name}</span>
          <button
            onClick={(e) => { e.stopPropagation(); onCloseTab(groupPath, tab.name); }}
            className={`ml-2 rounded w-5 h-5 flex items-center justify-center hover:bg-[#ffffff20] transition-opacity ${activeTabName === tab.name ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
              }`}
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
              <path fillRule="evenodd" clipRule="evenodd" d="M8 8.707l3.646 3.647.708-.707L8.707 8l3.647-3.646-.707-.708L8 7.293 4.354 3.646l-.707.708L7.293 8l-3.646 3.646.707.708L8 8.707z" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
};

const Editor = ({ activeFiles, addActiveFile, removeFile, showSettings, settingsContent, theme, language, wordWrap, editorFontSize }) => {
  // Mosaic node structure
  const [mosaicLayout, setMosaicLayout] = useState('group1');

  // Dropdown state
  const [breadcrumbDropdown, setBreadcrumbDropdown] = useState(null);

  // Track which group is actively focused by the user
  const [activeGroupId, setActiveGroupId] = useState('group1');

  // The state of tabs per group
  // Format: { 'group1': { tabs: [...activeFiles], activeTabName: 'file1.js' } }
  const [groupsData, setGroupsData] = useState({
    'group1': {
      tabs: [],
      activeTabName: null
    }
  });

  // Sync active files from parent into our groups concept
  useEffect(() => {
    setGroupsData(prev => {
      const newGroups = { ...prev };

      // Ensure active group exists
      if (!newGroups[activeGroupId]) {
        newGroups[activeGroupId] = { tabs: [], activeTabName: null };
      }

      const currentActiveTabs = newGroups[activeGroupId].tabs;

      // Check for newly added files (present in activeFiles but not in ANY group)
      const allExistingTabNames = new Set();
      Object.values(newGroups).forEach(g => g.tabs.forEach(t => allExistingTabNames.add(t.name)));

      const newFiles = activeFiles.filter(f => !allExistingTabNames.has(f.name));

      if (newFiles.length > 0) {
        // Add new files to the active group
        newGroups[activeGroupId].tabs = [...currentActiveTabs, ...newFiles];
        // Focus the last added file
        newGroups[activeGroupId].activeTabName = newFiles[newFiles.length - 1].name;
      }

      // Check for globally removed files (removed via sidebar `removeFile` or other methods outside our internal tabs closure)
      const globalActiveNames = new Set(activeFiles.map(f => f.name));

      Object.keys(newGroups).forEach(groupId => {
        const group = newGroups[groupId];
        const keptTabs = group.tabs.filter(t => globalActiveNames.has(t.name));

        if (keptTabs.length !== group.tabs.length) {
          group.tabs = keptTabs;
          // If active tab was removed
          if (!keptTabs.find(t => t.name === group.activeTabName)) {
            group.activeTabName = keptTabs.length > 0 ? keptTabs[0].name : null;
          }
        }
      });

      return newGroups;
    });
  }, [activeFiles, activeGroupId]);


  const handleTabClick = (groupId, tabName) => {
    setActiveGroupId(groupId);
    setGroupsData(prev => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        activeTabName: tabName
      }
    }));
  };

  const handleCloseTab = (groupId, tabName) => {
    removeFile(tabName); // Tell parent to remove it

    // Internal state update happens via the useEffect syncing activeFiles,
    // but we can proactively do it for responsiveness
    setGroupsData(prev => {
      const groupInfo = prev[groupId];
      const newTabs = groupInfo.tabs.filter(t => t.name !== tabName);
      let newActiveTab = groupInfo.activeTabName;

      if (newActiveTab === tabName) {
        newActiveTab = newTabs.length > 0 ? newTabs[0].name : null;
      }

      return {
        ...prev,
        [groupId]: {
          tabs: newTabs,
          activeTabName: newActiveTab
        }
      };
    });
  };

  const handleMosaicChange = (newNode) => {
    // When a split happens, React-Mosaic might invent new string IDs for the tree leaves if we let it
    // Or we simply track the tree structure
    setMosaicLayout(newNode);

    // Make sure we initialize group data for any new leaves created by split
    const leaves = getLeaves(newNode);
    setGroupsData(prev => {
      let changed = false;
      const nextGroups = { ...prev };
      leaves.forEach(leafId => {
        if (!nextGroups[leafId]) {
          nextGroups[leafId] = { tabs: [], activeTabName: null };
          changed = true;
        }
      });
      return changed ? nextGroups : prev;
    });
  };

  const renderTile = (groupId, path) => {
    const groupData = groupsData[groupId] || { tabs: [], activeTabName: null };
    const { tabs, activeTabName } = groupData;
    const activeTab = tabs.find(t => t.name === activeTabName);
    const isActiveGroup = groupId === activeGroupId;

    return (
      <MosaicWindow
        path={path}
        title=""
        createNode={() => `group_${Date.now()}`} // When splitting horizontally/vertically this ID is given to the new leaf
        className="bg-[var(--bg-secondary)] text-[var(--text-primary)]"
        toolbarControls={[]} // Hide completely, we use our own TabBar
        onDragStart={() => setActiveGroupId(groupId)}
      >
        <div
          className="flex flex-col h-full w-full bg-[var(--bg-primary)]"
          onClick={() => setActiveGroupId(groupId)} // Focus group on click
        >
          {tabs.length > 0 && (
            <TabBar
              tabs={tabs}
              activeTabName={activeTabName}
              onTabClick={handleTabClick}
              onCloseTab={handleCloseTab}
              isActiveGroup={isActiveGroup}
              groupPath={groupId}
            />
          )}

          <div className="flex-1 overflow-hidden relative" onClick={() => setBreadcrumbDropdown(null)}>
            {/* Breadcrumbs */}
            {activeTab && (
              <div className="flex items-center px-4 py-1.5 text-[13px] font-sans text-[#cccccc] bg-[var(--bg-primary)] opacity-80 relative" style={{ borderBottom: '1px solid var(--border-color, #2b2b2b)', zIndex: 10 }}>
                <span className="opacity-70 hover:opacity-100 cursor-pointer hover:bg-[#2a2d2e] px-1 rounded transition-colors" title="Local Workspace" onClick={(e) => { e.stopPropagation(); setBreadcrumbDropdown(breadcrumbDropdown === 'cv_project' ? null : 'cv_project'); }}>cv_project</span>
                {breadcrumbDropdown === 'cv_project' && (
                  <div className="absolute top-[28px] left-[16px] bg-[#252526] border border-[#454545] shadow-lg rounded py-1 z-50 min-w-[200px] text-[13px] font-sans text-[#cccccc]" onClick={e => e.stopPropagation()}>
                    <div className="px-4 py-1 hover:bg-[#04395e] cursor-pointer flex items-center">
                      <span className="opacity-50 mr-2 text-[10px]">▶</span> backend
                    </div>
                    <div className="px-4 py-1 hover:bg-[#04395e] cursor-pointer flex items-center">
                      <span className="opacity-50 mr-2 text-[10px]">▶</span> frontend
                    </div>
                    <div className="px-4 py-1 hover:bg-[#04395e] cursor-pointer flex items-center">
                      <DocumentIcon color="#69b0fa" /> README.md
                    </div>
                  </div>
                )}

                <span className="mx-1 opacity-50">›</span>
                <span className="opacity-70 hover:opacity-100 cursor-pointer hover:bg-[#2a2d2e] px-1 rounded transition-colors" title="Navigate to frontend" onClick={(e) => { e.stopPropagation(); setBreadcrumbDropdown(breadcrumbDropdown === 'frontend' ? null : 'frontend'); }}>frontend</span>
                {breadcrumbDropdown === 'frontend' && (
                  <div className="absolute top-[28px] left-[90px] bg-[#252526] border border-[#454545] shadow-lg rounded py-1 z-50 min-w-[200px] text-[13px] font-sans text-[#cccccc]" onClick={e => e.stopPropagation()}>
                    <div className="px-4 py-1 hover:bg-[#04395e] cursor-pointer flex items-center">
                      <span className="opacity-50 mr-2 text-[10px]">▶</span> build
                    </div>
                    <div className="px-4 py-1 hover:bg-[#04395e] cursor-pointer flex items-center">
                      <span className="opacity-50 mr-2 text-[10px]">▶</span> node_modules
                    </div>
                    <div className="px-4 py-1 hover:bg-[#04395e] cursor-pointer flex items-center">
                      <span className="opacity-50 mr-2 text-[10px]">▶</span> public
                    </div>
                    <div className="px-4 py-1 hover:bg-[#04395e] cursor-pointer flex items-center bg-[#04395e] bg-opacity-40">
                      <span className="opacity-50 mr-2 text-[10px]">▶</span> src
                    </div>
                    <div className="px-4 py-1 hover:bg-[#04395e] cursor-pointer flex items-center">
                      <DocumentIcon color="#cbcb41" /> package.json
                    </div>
                    <div className="px-4 py-1 hover:bg-[#04395e] cursor-pointer flex items-center">
                      <DocumentIcon color="#cbcb41" /> tailwind.config.js
                    </div>
                  </div>
                )}

                <span className="mx-1 opacity-50">›</span>
                <span className="opacity-70 hover:opacity-100 cursor-pointer hover:bg-[#2a2d2e] px-1 rounded transition-colors" title={`Expand ${activeTab.section}`}>{activeTab.section}</span>
                <span className="mx-1 opacity-50">›</span>
                <span className="hover:opacity-100 cursor-pointer text-[#e8e8e8] hover:bg-[#2a2d2e] px-1 rounded transition-colors" title={`View ${activeTab.name}`}>{activeTab.name}</span>
              </div>
            )}

            {/* Editor Content Area */}
            <div className="h-[calc(100%-28px)] w-full">
              {activeTab ? (
                activeTab.name === 'settings.json' ? (
                  <div className="h-full overflow-auto custom-scrollbar">
                    {settingsContent}
                  </div>
                ) : (
                  <EditorPanel file={activeTab} content={activeTab.content} theme={theme} wordWrap={wordWrap} editorFontSize={editorFontSize} />
                )
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-[var(--text-secondary)] opacity-30 select-none bg-[#1e1e1e]">
                  <svg width="128" height="128" viewBox="0 0 128 128" fill="currentColor">
                    <path d="M109.91,24.1L82.16,3.61c-1.88-1.39-4.32-1.39-6.2,0L3.63,56.88c-1.88,1.39-1.88,3.65,0,5.04l23.51,17.38L3.63,96.67
                      c-1.88,1.39-1.88,3.65,0,5.04l72.33,53.27c1.88,1.39,4.32,1.39,6.2,0l27.75-20.49c1.88-1.39,1.88-3.65,0-5.04l-23.51-17.38
                      l23.51-17.38C111.79,27.75,111.79,25.49,109.91,24.1z M85.26,16.8l16.11,11.85l-16.11,11.85V16.8z M75.95,30l-28.7,21.05
                      L25.33,34.9L75.95,1z M75.95,108.99L25.33,71.55l21.92-16.15L75.95,76.45V108.99z M85.26,92.19v-23.7l16.11,11.85L85.26,92.19z
                      M80.6,67.65l-26.6-19.53l26.6-19.53l26.6,19.53L80.6,67.65z"/>
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>
      </MosaicWindow>
    );
  };

  return (
    <div className="h-full w-full">
      <Mosaic
        renderTile={renderTile}
        initialValue={mosaicLayout}
        onChange={handleMosaicChange}
        className="mosaic-editor-theme bg-[var(--bg-primary)]"
      />
    </div>
  );
};

export default Editor;
